import { Link } from 'react-router-dom';
import { Search, Sparkles, Filter, PenLine, Rocket, Smartphone, BarChart3, Play, Star, ArrowRight, Users, Building2, TrendingUp, Shield } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const features = [
  {
    Icon: Search,
    title: 'AI Lead Discovery',
    desc: 'Automatically find and qualify high-intent prospects using AI-powered signals from across the web.',
    color: 'var(--sky)',
    bg: 'var(--sky-light)',
  },
  {
    Icon: Filter,
    title: 'Smart Segmentation',
    desc: 'Build dynamic audience segments based on firmographics, technographics, and behavioral data.',
    color: 'var(--green)',
    bg: 'var(--green-light)',
  },
  {
    Icon: PenLine,
    title: 'AI Outreach Assistant',
    desc: 'Generate hyper-personalized emails, LinkedIn messages, and call scripts in seconds.',
    color: 'var(--amber)',
    bg: 'var(--amber-light)',
  },
  {
    Icon: Rocket,
    title: 'Campaign Engine',
    desc: 'Orchestrate multi-channel sequences with automated follow-ups and smart scheduling.',
    color: '#7C3AED',
    bg: '#EDE9FE',
  },
  {
    Icon: Smartphone,
    title: 'Mobile Inbox',
    desc: 'Manage replies, approve messages, and track engagement from anywhere on your phone.',
    color: '#E53E6B',
    bg: '#FDE8EE',
  },
  {
    Icon: BarChart3,
    title: 'Analytics & Learning',
    desc: 'Track open rates, reply rates, and conversions. AI learns what works and optimizes over time.',
    color: 'var(--sky)',
    bg: 'var(--sky-light)',
  }
];

const steps = [
  { num: '1', title: 'Connect', desc: 'Link your CRM, email, and LinkedIn accounts in minutes.' },
  { num: '2', title: 'Discover', desc: 'AI scans millions of data points to surface your ideal prospects.' },
  { num: '3', title: 'Engage', desc: 'Launch personalized multi-channel outreach campaigns.' },
  { num: '4', title: 'Convert', desc: 'Track engagement and close deals with actionable insights.' }
];

const testimonials = [
  {
    name: 'Sarah Chen',
    title: 'VP of Sales',
    company: 'TechFlow',
    quote: 'LeadNova transformed our outbound pipeline. We went from 50 to 200 qualified meetings per month in just 3 months.',
    avatar: 'SC'
  },
  {
    name: 'Marcus Johnson',
    title: 'Head of Growth',
    company: 'ScaleUp',
    quote: 'The AI outreach assistant writes better emails than most of my reps. Response rates jumped 3x since we started using it.',
    avatar: 'MJ'
  },
  {
    name: 'Emily Rodriguez',
    title: 'Sales Director',
    company: 'CloudBase',
    quote: 'Finally, a tool that combines lead discovery and outreach in one place. Our team saves 15 hours per week on prospecting.',
    avatar: 'ER'
  }
];

const logos = ['Acme Corp', 'TechFlow', 'ScaleUp', 'CloudBase', 'DataSync'];

const heroStats = [
  { icon: Users, value: '210M+', label: 'Verified Contacts' },
  { icon: Building2, value: '35M+', label: 'Companies' },
  { icon: TrendingUp, value: '3x', label: 'More Replies' },
  { icon: Shield, value: '98%', label: 'Data Accuracy' },
];

export default function HomePage() {
  const { isAuthenticated } = useAuth();
  return (
    <div className="hp">
      <section className="hp-hero">
        <div className="hp-hero-bg">
          <div className="hp-hero-gradient" />
          <div className="hp-hero-dots" />
        </div>

        <div className="container hp-hero-inner">
          <div className="hp-hero-content">
            <div className="hp-badge">
              <span className="hp-badge-new">New</span>
              AI-powered prospecting engine now available
              <ArrowRight size={14} />
            </div>

            <h1 className="hp-hero-title">
              Discover Leads.<br />
              Craft Outreach.<br />
              <span style={{ color: 'var(--green)' }}>Close Deals.</span>
            </h1>

            <p className="hp-hero-subtitle">
              LeadNova combines AI-powered lead discovery with intelligent outreach automation to help your sales team book more meetings and close more deals.
            </p>

            <div className="hp-hero-cta">
              {!isAuthenticated && (
                <Link to="/pricing" className="hp-btn-primary">
                  Start Free Trial <ArrowRight size={18} />
                </Link>
              )}
              <Link to="/product" className="hp-btn-secondary">
                <Play size={16} /> Watch Demo
              </Link>
            </div>
            <p className="hp-hero-note">
              Free 14-day trial &middot; No credit card required
            </p>
          </div>

          <div className="hp-stats-bar">
            {heroStats.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="hp-stat-item">
                  <div className="hp-stat-icon">
                    <Icon size={20} color="var(--sky)" />
                  </div>
                  <div>
                    <div className="hp-stat-value">{s.value}</div>
                    <div className="hp-stat-label">{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="hp-logos-section">
        <div className="container" style={{ textAlign: 'center' }}>
          <p className="hp-logos-label">Trusted by 2,000+ sales teams worldwide</p>
          <div className="hp-logos-bar">
            {logos.map((name) => (
              <div key={name} className="hp-logo-item">{name}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="hp-section hp-features-section">
        <div style={{
          position: 'absolute', top: -200, left: -200, width: 500, height: 500,
          background: 'radial-gradient(circle, rgba(77,184,232,0.05), transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -100, right: -100, width: 400, height: 400,
          background: 'radial-gradient(circle, rgba(58,170,53,0.04), transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="hp-section-header">
            <div className="hp-pill" style={{ background: 'var(--sky-light)', color: 'var(--sky)' }}>
              <Sparkles size={14} />
              Platform Features
            </div>
            <h2 className="hp-section-title">Everything you need for outbound</h2>
            <p className="hp-section-desc">
              From finding prospects to closing deals, LeadNova gives your team the tools to build a predictable pipeline.
            </p>
          </div>

          <div className="hp-features-grid">
            {features.map((f) => {
              const FIcon = f.Icon;
              return (
                <div key={f.title} className="hp-feature-card">
                  <div className="hp-feature-icon" style={{ background: f.bg, color: f.color }}>
                    <FIcon size={22} />
                  </div>
                  <div>
                    <h3 className="hp-feature-title">{f.title}</h3>
                    <p className="hp-feature-desc">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="hp-section hp-steps-section">
        <div className="hp-steps-bg-dots" />
        <div className="hp-steps-bg-glow" />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="hp-section-header">
            <div className="hp-pill" style={{ background: 'rgba(77,184,232,0.12)', color: 'var(--sky)', border: '1px solid rgba(77,184,232,0.2)' }}>
              <Rocket size={14} />
              Quick Setup
            </div>
            <h2 className="hp-section-title" style={{ color: '#fff' }}>How it works</h2>
            <p className="hp-section-desc" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Get started in minutes, not weeks. Four simple steps to transform your outbound.
            </p>
          </div>

          <div className="hp-steps-row">
            <div className="hp-steps-line" />
            {steps.map((s, i) => {
              const stepIcons = [Search, Sparkles, Rocket, TrendingUp];
              const StepIcon = stepIcons[i];
              const stepColors = ['var(--sky)', 'var(--amber)', 'var(--green)', '#A78BFA'];
              return (
                <div key={s.title} className="hp-step">
                  <div className="hp-step-icon-wrap">
                    <StepIcon size={24} color={stepColors[i]} />
                    <div className="hp-step-num" style={{ background: stepColors[i] }}>{s.num}</div>
                  </div>
                  <h3 className="hp-step-title">{s.title}</h3>
                  <p className="hp-step-desc">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="hp-section hp-testimonials-section">
        <div className="container">
          <div className="hp-section-header">
            <div className="hp-pill" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>
              <Star size={14} fill="var(--amber)" color="var(--amber)" />
              Customer Stories
            </div>
            <h2 className="hp-section-title">Loved by sales teams everywhere</h2>
            <p className="hp-section-desc">
              See why 2,000+ teams trust LeadNova to power their outbound pipeline.
            </p>
          </div>

          <div className="hp-testimonials-grid">
            {testimonials.map((t, idx) => {
              const isDark = idx === 1;
              return (
                <div key={t.name} className={`hp-testimonial ${isDark ? 'hp-testimonial-dark' : ''}`}>
                  <div>
                    <div className="hp-stars">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={15} fill="var(--amber)" color="var(--amber)" />
                      ))}
                    </div>
                    <p className="hp-quote">"{t.quote}"</p>
                  </div>
                  <div className="hp-testimonial-author">
                    <div className={`hp-avatar ${isDark ? 'hp-avatar-dark' : ''}`}>{t.avatar}</div>
                    <div>
                      <div className="hp-author-name">{t.name}</div>
                      <div className="hp-author-role">{t.title}, {t.company}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hp-bottom-stats">
            {[
              { value: '3x', label: 'More qualified meetings' },
              { value: '15hrs', label: 'Saved per week per rep' },
              { value: '47%', label: 'Higher response rates' },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div className="hp-bottom-stat-value">{s.value}</div>
                <div className="hp-bottom-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="hp-cta-section">
        <div className="hp-cta-gradient-top" />
        <div className="hp-cta-glow" />
        <div className="container hp-cta-content">
          <h2 className="hp-cta-title">Ready to supercharge your pipeline?</h2>
          <p className="hp-cta-desc">
            Join 2,000+ sales teams already using LeadNova to crush their quotas.
          </p>
          {!isAuthenticated && (
            <Link to="/pricing" className="hp-btn-primary">
              Start Free Trial <ArrowRight size={18} />
            </Link>
          )}
        </div>
        <div className="hp-cta-fade" />
      </section>

      <style>{`
        .hp { overflow-x: hidden; }

        .hp-hero {
          background: var(--navy);
          position: relative;
          padding-top: 72px;
          padding-bottom: 20px;
        }
        .hp-hero-bg { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
        .hp-hero-gradient {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(77,184,232,0.15), transparent);
        }
        .hp-hero-dots {
          position: absolute; inset: 0; opacity: 0.03;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .hp-hero-inner { position: relative; z-index: 1; }
        .hp-hero-content {
          text-align: center;
          padding: 80px 0 48px;
        }

        .hp-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(77,184,232,0.12); border: 1px solid rgba(77,184,232,0.25);
          border-radius: 100px; padding: 6px 20px 6px 8px;
          margin-bottom: 32px; font-size: 13px; font-weight: 500; color: var(--sky);
        }
        .hp-badge-new {
          background: var(--green); color: #fff; font-size: 11px; font-weight: 700;
          padding: 2px 10px; border-radius: 100px; text-transform: uppercase; letter-spacing: 0.5px;
        }

        .hp-hero-title {
          font-size: 58px; font-weight: 800; color: #fff;
          line-height: 1.08; max-width: 780px; margin: 0 auto 24px;
          letter-spacing: -0.03em;
        }
        .hp-hero-subtitle {
          font-size: 19px; color: rgba(255,255,255,0.6);
          max-width: 560px; margin: 0 auto 40px; line-height: 1.7;
        }

        .hp-hero-cta {
          display: flex; gap: 14px; justify-content: center;
          flex-wrap: wrap; margin-bottom: 16px;
        }
        .hp-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--green); color: #fff;
          font-size: 16px; font-weight: 600; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          transition: all 0.2s; box-shadow: 0 4px 14px rgba(58,170,53,0.4);
        }
        .hp-btn-primary:hover { text-decoration: none; opacity: 0.9; }
        .hp-btn-secondary {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.9);
          font-size: 16px; font-weight: 500; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          border: 1px solid rgba(255,255,255,0.15); transition: all 0.2s;
        }
        .hp-btn-secondary:hover { text-decoration: none; background: rgba(255,255,255,0.12); }
        .hp-hero-note { font-size: 13px; color: rgba(255,255,255,0.35); margin-bottom: 0; }

        .hp-stats-bar {
          max-width: 820px; margin: 0 auto;
          background: linear-gradient(135deg, #1e3a6e 0%, #254a82 50%, #1a3568 100%);
          border: 1px solid rgba(77,184,232,0.2); border-radius: 16px;
          padding: 28px 40px;
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px;
          margin-bottom: -44px; position: relative; z-index: 2;
          box-shadow: 0 12px 40px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.1);
        }
        .hp-stat-item { display: flex; align-items: center; gap: 14px; }
        .hp-stat-icon {
          width: 42px; height: 42px; border-radius: 10px;
          background: rgba(77,184,232,0.15);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .hp-stat-value { font-size: 22px; font-weight: 700; color: #fff; letter-spacing: -0.02em; }
        .hp-stat-label { font-size: 12px; color: rgba(255,255,255,0.45); font-weight: 500; }

        .hp-logos-section { padding: 80px 0 40px; background: var(--gray-50); }
        .hp-logos-label {
          font-size: 13px; font-weight: 500; color: var(--gray-500);
          text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 24px;
        }
        .hp-logos-bar { display: flex; gap: 48px; justify-content: center; align-items: center; flex-wrap: wrap; }
        .hp-logo-item { font-size: 17px; font-weight: 700; color: var(--gray-500); opacity: 0.4; letter-spacing: 0.5px; }

        .hp-section { padding: 100px 0; position: relative; overflow: hidden; }
        .hp-features-section { background: #fff; }
        .hp-steps-section { background: var(--navy); }
        .hp-testimonials-section { background: var(--gray-50); }

        .hp-steps-bg-dots {
          position: absolute; inset: 0; opacity: 0.03;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 28px 28px;
        }
        .hp-steps-bg-glow {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(77,184,232,0.1), transparent 60%);
          pointer-events: none;
        }

        .hp-section-header { text-align: center; margin-bottom: 64px; }
        .hp-pill {
          display: inline-flex; align-items: center; gap: 8px;
          border-radius: 100px; padding: 6px 16px; margin-bottom: 16px;
          font-size: 13px; font-weight: 600;
        }
        .hp-section-title {
          font-size: 38px; font-weight: 800; color: var(--navy);
          margin-bottom: 14px; letter-spacing: -0.02em;
        }
        .hp-section-desc {
          font-size: 17px; color: var(--gray-500);
          max-width: 540px; margin: 0 auto; line-height: 1.6;
        }

        .hp-features-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 0;
          border-radius: 20px; overflow: hidden;
          border: 1px solid var(--gray-200);
          box-shadow: 0 4px 32px rgba(0,0,0,0.04);
        }
        .hp-feature-card {
          padding: 36px; background: #fff;
          display: flex; gap: 20px; align-items: flex-start;
          border-bottom: 1px solid var(--gray-100);
        }
        .hp-feature-card:nth-child(odd) { border-right: 1px solid var(--gray-100); }
        .hp-feature-card:nth-last-child(-n+2) { border-bottom: none; }
        .hp-feature-icon {
          width: 48px; height: 48px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .hp-feature-title { font-size: 17px; font-weight: 700; color: var(--navy); margin-bottom: 6px; }
        .hp-feature-desc { font-size: 14px; color: var(--gray-500); line-height: 1.65; margin: 0; }

        .hp-steps-row { display: flex; align-items: flex-start; gap: 0; position: relative; }
        .hp-steps-line {
          position: absolute; top: 32px; left: 12%; width: 76%; height: 2px;
          background: linear-gradient(to right, rgba(77,184,232,0.4), rgba(58,170,53,0.4));
          z-index: 0;
        }
        .hp-step { flex: 1; text-align: center; position: relative; z-index: 1; padding: 0 12px; }
        .hp-step-icon-wrap {
          width: 64px; height: 64px; border-radius: 16px;
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 20px; position: relative;
        }
        .hp-step-num {
          position: absolute; top: -6px; right: -6px;
          width: 24px; height: 24px; border-radius: 50%;
          color: #fff; font-size: 12px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
        }
        .hp-step-title { font-size: 18px; font-weight: 700; color: #fff; margin-bottom: 8px; }
        .hp-step-desc { font-size: 14px; color: rgba(255,255,255,0.5); line-height: 1.6; }

        .hp-testimonials-grid {
          display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0;
          border-radius: 20px; overflow: hidden;
          box-shadow: 0 4px 32px rgba(0,0,0,0.08);
        }
        .hp-testimonial {
          background: #fff; padding: 44px 36px;
          display: flex; flex-direction: column; justify-content: space-between;
        }
        .hp-testimonial-dark { background: var(--navy); }
        .hp-stars { display: flex; gap: 3px; margin-bottom: 20px; }
        .hp-quote {
          font-size: 17px; line-height: 1.7; margin-bottom: 32px; font-weight: 400;
          color: var(--gray-900);
        }
        .hp-testimonial-dark .hp-quote { color: rgba(255,255,255,0.9); }
        .hp-testimonial-author {
          display: flex; align-items: center; gap: 14px;
          border-top: 1px solid var(--gray-100); padding-top: 20px;
        }
        .hp-testimonial-dark .hp-testimonial-author { border-top-color: rgba(255,255,255,0.1); }
        .hp-avatar {
          width: 46px; height: 46px; border-radius: 50%;
          background: var(--sky-light); color: var(--sky);
          display: flex; align-items: center; justify-content: center;
          font-size: 15px; font-weight: 700; flex-shrink: 0;
        }
        .hp-avatar-dark { background: rgba(77,184,232,0.2); }
        .hp-author-name { font-weight: 600; font-size: 15px; color: var(--gray-900); }
        .hp-testimonial-dark .hp-author-name { color: #fff; }
        .hp-author-role { font-size: 13px; color: var(--gray-500); }
        .hp-testimonial-dark .hp-author-role { color: rgba(255,255,255,0.5); }

        .hp-bottom-stats { display: flex; justify-content: center; gap: 40px; margin-top: 48px; flex-wrap: wrap; }
        .hp-bottom-stat-value { font-size: 32px; font-weight: 800; color: var(--green); letter-spacing: -0.02em; }
        .hp-bottom-stat-label { font-size: 14px; color: var(--gray-500); font-weight: 500; }

        .hp-cta-section {
          background: var(--navy); padding: 80px 0 0;
          text-align: center; color: white;
          position: relative; overflow: hidden; margin-bottom: -1px;
        }
        .hp-cta-gradient-top {
          position: absolute; top: 0; left: 0; right: 0; height: 160px;
          background: linear-gradient(to bottom, var(--gray-50) 0%, rgba(26,45,90,0.3) 40%, rgba(26,45,90,0.7) 70%, var(--navy) 100%);
          z-index: 0;
        }
        .hp-cta-glow {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(77,184,232,0.12), transparent);
        }
        .hp-cta-content { position: relative; z-index: 1; padding-bottom: 80px; }
        .hp-cta-title { font-size: 38px; font-weight: 700; margin-bottom: 16px; letter-spacing: -0.02em; }
        .hp-cta-desc {
          font-size: 18px; color: rgba(255,255,255,0.6);
          max-width: 500px; margin: 0 auto 32px;
        }
        .hp-cta-fade {
          height: 80px;
          background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.03));
        }

        /* ========== TABLET (768px) ========== */
        @media (max-width: 768px) {
          .hp-hero-content { padding: 48px 0 32px; }
          .hp-hero-title { font-size: 40px; }
          .hp-hero-subtitle { font-size: 16px; margin: 0 auto 32px; }

          .hp-stats-bar {
            grid-template-columns: 1fr 1fr;
            padding: 20px; gap: 16px;
            margin-bottom: -30px;
          }

          .hp-section { padding: 64px 0; }
          .hp-section-header { margin-bottom: 40px; }
          .hp-section-title { font-size: 28px; }
          .hp-section-desc { font-size: 15px; }

          .hp-features-grid { grid-template-columns: 1fr; border-radius: 12px; }
          .hp-feature-card { padding: 24px 20px; }
          .hp-feature-card:nth-child(odd) { border-right: none; }
          .hp-feature-card:not(:last-child) { border-bottom: 1px solid var(--gray-100); }

          .hp-steps-row { flex-direction: column; gap: 32px; }
          .hp-steps-line { display: none; }
          .hp-step { padding: 0; }

          .hp-testimonials-grid { grid-template-columns: 1fr; border-radius: 12px; }
          .hp-testimonial { padding: 28px 24px; }

          .hp-bottom-stats { gap: 24px; margin-top: 32px; }
          .hp-bottom-stat-value { font-size: 26px; }

          .hp-logos-section { padding: 56px 0 32px; }
          .hp-logos-bar { gap: 24px; }

          .hp-cta-title { font-size: 28px; }
          .hp-cta-desc { font-size: 16px; }
          .hp-cta-content { padding-bottom: 48px; }
        }

        /* ========== SMALL MOBILE (480px) ========== */
        @media (max-width: 480px) {
          .hp-hero-content { padding: 32px 0 24px; }
          .hp-hero-title { font-size: 32px; line-height: 1.15; }
          .hp-hero-subtitle { font-size: 15px; margin: 0 auto 28px; }
          .hp-badge { font-size: 12px; padding: 5px 14px 5px 6px; margin-bottom: 24px; }

          .hp-hero-cta { flex-direction: column; align-items: stretch; }
          .hp-btn-primary, .hp-btn-secondary {
            justify-content: center; padding: 12px 24px; font-size: 15px;
          }

          .hp-stats-bar {
            grid-template-columns: 1fr; padding: 16px;
            gap: 12px; margin-bottom: -20px; border-radius: 12px;
          }
          .hp-stat-value { font-size: 18px; }

          .hp-section { padding: 48px 0; }
          .hp-section-header { margin-bottom: 32px; }
          .hp-section-title { font-size: 24px; }
          .hp-section-desc { font-size: 14px; }

          .hp-feature-card { padding: 20px 16px; gap: 14px; }
          .hp-feature-icon { width: 40px; height: 40px; border-radius: 10px; }

          .hp-testimonial { padding: 24px 18px; }
          .hp-quote { font-size: 15px; margin-bottom: 24px; }

          .hp-logos-section { padding: 40px 0 24px; }
          .hp-logos-bar { gap: 16px; }
          .hp-logo-item { font-size: 14px; }

          .hp-cta-title { font-size: 24px; }
          .hp-cta-desc { font-size: 15px; }
          .hp-cta-content { padding-bottom: 40px; }

          .hp-bottom-stat-value { font-size: 24px; }
          .hp-bottom-stat-label { font-size: 13px; }
        }
      `}</style>
    </div>
  );
}
